import { formatFilterValue } from "@/utils/formatters";
import { Table } from "@tanstack/react-table";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useGenerateTermFilterActions } from "./actions";
import { useFilterState } from "./filterState";
import { ALL_OPTIONS } from "../../data/options";

export const useToolbarFilters = <TData>(table: Table<TData>) => {
  const { updateFilter } = useGenerateTermFilterActions();
  const filterState = useFilterState();

  const [searchValue, setSearchValue] = useState("");
  const [storeValue, setStoreValue] = useState<any[]>([]);
  const [statusValue, setStatusValue] = useState(ALL_OPTIONS);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  useEffect(() => {
    const filters = (filterState as any)?.filters || {};
    if (filters.search) setSearchValue(filters.search);
    if (filters.store_code) setStoreValue(filters.store_code);
    if (filters.status) setStatusValue(filters.status);
    if (filters.start_date && filters.end_date) {
      setDateRange({
        from: new Date(filters.start_date),
        to: new Date(filters.end_date),
      });
    }
  }, []);

  const debouncedUpdateFilter = useCallback(
    debounce((value: string) => {
      updateFilter({ key: "search", value });
      updateFilter({ key: "meta", value: { page: 1 } });
    }, 500),
    [updateFilter],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 11);
    setSearchValue(value);
    debouncedUpdateFilter(value);
  };

  const handleStoreChange = (options: any) => {
    const selectedOptions = Array.isArray(options)
      ? options
      : options
        ? [options]
        : [];
    setStoreValue(selectedOptions);
    const values = selectedOptions.map((opt: any) => opt.value);
    updateFilter({ key: "store_code", value: formatFilterValue(values) || "" });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handleStatusChange = (value: string) => {
    setStatusValue(value);
    updateFilter({ key: "status", value: value === "all" ? "" : value });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handleDateRangeChange = useCallback(
    (startDate: string | undefined, endDate: string | undefined) => {
      setDateRange({
        from: startDate ? new Date(startDate) : undefined,
        to: endDate ? new Date(endDate) : undefined,
      });
      updateFilter({ key: "start_date", value: startDate || "" });
      updateFilter({ key: "end_date", value: endDate || "" });
      updateFilter({ key: "meta", value: { page: 1 } });
    },
    [updateFilter],
  );

  const clearAllFilters = (dateRangePickerClear?: () => void) => {
    setSearchValue("");
    setStoreValue([]);
    setStatusValue("all");
    setDateRange(undefined);
    dateRangePickerClear?.();
    updateFilter({ key: "search", value: "" });
    updateFilter({ key: "store_code", value: "" });
    updateFilter({ key: "status", value: "" });
    updateFilter({ key: "start_date", value: "" });
    updateFilter({ key: "end_date", value: "" });
    updateFilter({ key: "orderBy", value: {} });
    updateFilter({ key: "meta", value: { page: 1 } });
    table.resetColumnFilters();
  };

  return {
    searchValue,
    storeValue,
    statusValue,
    dateRange,
    handleInputChange,
    handleStoreChange,
    handleStatusChange,
    handleDateRangeChange,
    clearAllFilters,
  };
};
